'use client';

import React, { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

export default function RouteNavigationProgressBar() {
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const [visible, setVisible] = useState(false);
	const [progress, setProgress] = useState(0);

	const timerRef = useRef<number | null>(null);
	const finishingRef = useRef<number | null>(null);

	const clearTimers = () => {
		if (timerRef.current) {
			window.clearInterval(timerRef.current);
		}
		if (finishingRef.current) {
			window.clearTimeout(finishingRef.current);
		}
		timerRef.current = null;
		finishingRef.current = null;
	};

	const start = () => {
		clearTimers();
		setVisible(true);
		setProgress(8);

		timerRef.current = window.setInterval(() => {
			setProgress((p) => {
				if (p >= 90) {
					return p;
				}
				const inc = p < 40 ? 10 : p < 70 ? 4 : 1.5;
				return Math.min(90, p + inc);
			});
		}, 120);
	};

	const finish = () => {
		clearTimers();
		setProgress(100);
		finishingRef.current = window.setTimeout(() => {
			setVisible(false);
			setProgress(0);
		}, 220);
	};

	useEffect(() => {
		const onClickCapture = (e: MouseEvent) => {
			if (e.button !== 0) {
				return;
			}
			if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
				return;
			}

			const target = e.target as HTMLElement | null;
			const anchor = target?.closest?.('a') as HTMLAnchorElement | null;
			if (!anchor) {
				return;
			}

			const href = anchor.getAttribute('href') || '';
			if (!href || href.startsWith('#')) {
				return;
			}
			if (href.startsWith('mailto:') || href.startsWith('tel:')) {
				return;
			}
			if (anchor.target && anchor.target !== '_self') {
				return;
			}
			if (anchor.hasAttribute('download')) {
				return;
			}

			try {
				const url = new URL(href, window.location.href);
				if (url.origin !== window.location.origin) {
					return;
				}
				if (url.href === window.location.href) {
					return;
				}
			} catch {
				// ignore
			}

			start();
		};

		document.addEventListener('click', onClickCapture, true);
		return () => document.removeEventListener('click', onClickCapture, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!visible) {
			return;
		}
		finish();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, searchParams]);

	if (!visible) {
		return null;
	}

	return (
		<div className="route-progress" role="progressbar" aria-label="Loading">
			<div className="route-progress__bar" style={{ transform: `scaleX(${progress / 100})` }} />
		</div>
	);
}
